import * as React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import toast from 'react-hot-toast';
import BreadcrumbsWrapper from '../../../components/breadcrumbs' 


import { postRequest } from '../../../lib/api'; // Ensure this method exists
import { AuthContext } from '../../../authContext/context';
import { checkPermission } from '../../../utils/checkPermissions';
import { Typography } from '@mui/material';

export default function EditCustomer() {
  const { id } = useParams(); // Customer ID from route param
  const navigate = useNavigate();
  const authCtx = React.useContext(AuthContext);
  const [loading, setLoading] = React.useState(false);
  const userPermissions = authCtx.permissions; // Changed from authCtx.user?.permissions
  const isSuperAdmin = authCtx.isSuperAdmin;
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  // Check edit permission
  const canEdit = isSuperAdmin || checkPermission(userPermissions, 'customers', 'edit');

React.useEffect(() => {
  if (canEdit) {
    const fetchCustomerById = async () => {
      setLoading(true);
      try {
        const response = await postRequest(`private/singleCustomer/${id}`, {},authCtx.token);
        const customer = response?.data;

        if (customer) {
          reset({
            title: customer?.title || '',
            forename: customer?.forename || '',
            surname: customer?.surname || '',
            dob: customer?.dob || '',
            email: customer?.email || '',
            //phone_number: customer?.phone_number || '',
            address1: customer?.address?.address1 || '',
            address2: customer?.address?.address2 || '',
            town: customer?.address?.town || '',
            country: customer?.address?.country || '',
            postcode: customer?.address?.postcode || '',
          });
        }
      } catch (error) {
        toast.error(error?.message || 'Failed to fetch customer');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerById();
  }
}, [id, reset, authCtx.token, canEdit]);


  const onSubmit = async (data) => {
    const payload = {
      title: data?.title,
      dob: data?.dob,
      email: data?.email,
      forename: data?.forename,
      surname: data?.surname,
      //phone_number: data?.phone_number,
      address: {
        address1: data?.address1,
        address2: data?.address2,
        town: data?.town,
        country: data?.country,
        postcode: data?.postcode,
      }
    };

    setLoading(true);
    try {
      const response = await postRequest(`private/editCustomer/${id}`, payload, authCtx.token);
      if (response?.status === 200) {
        //toast.success('Customer updated successfully!');
          
        navigate('/customers');
      } else {
        //toast.error(response?.message || 'Update failed');
      }
    } catch (error) {
     // toast.error(error?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  if (!canEdit) {
    return (
      <>
        <BreadcrumbsWrapper
          items={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Customers', path: '/customers' },
            { label: 'Edit Customer', path: '' },
          ]}
        />
        <br></br>
        <div className='page-header pb-3'>
          <h1 className='mt-0 mb-0'>Edit Customer</h1>
        </div>
        <div className='page-top-formview mb-3 text-center'>
          <Typography variant="h6" color="text.secondary">
            You do not have permission to edit customers.
          </Typography>
        </div>
      </>
    );
  }

  return (
    <>
       <BreadcrumbsWrapper
            items={[
              { label: 'Dashboard', path: '/dashboard' },
              { label: 'Customers', path: '/customers' },
              { label: 'Edit Customer', path: null }
            ]}
          />
          <br></br>
      <div className="page-header">
        <h1 className="mt-0">Edit Customer</h1>
      </div>

      <div className="page-top-formview mb-3">
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="title">
              <Form.Label>Title</Form.Label>
              <Form.Control type="text" placeholder="Mr/Ms" {...register('title')} />
            </Form.Group>

            <Form.Group as={Col} controlId="forename">
              <Form.Label>Forename</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter forename"
                {...register('forename', { required: 'Forename is required' })}
                isInvalid={!!errors.forename}
              />
              <Form.Control.Feedback type="invalid">
                {errors.forename?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} controlId="surname">
              <Form.Label>Surname</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter surname"
                {...register('surname', { required: 'Surname is required' })}
                isInvalid={!!errors.surname}
              />
              <Form.Control.Feedback type="invalid">
                {errors.surname?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} controlId="email">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Invalid email address',
                  },
                })}
                isInvalid={!!errors.email}
              />
              <Form.Control.Feedback type="invalid">
                {errors.email?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} controlId="dob">
              <Form.Label>Date of Birth</Form.Label>
              <Form.Control
                type="date"
                {...register('dob')}
              />
            </Form.Group>
          </Row>

          <Form.Label>Address</Form.Label>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="line1">
              <Form.Control type="text" placeholder="Line 1" {...register('address1')} />
            </Form.Group>
            <Form.Group as={Col} controlId="line2">
              <Form.Control type="text" placeholder="Line 2" {...register('address2')} />
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} controlId="town">
              <Form.Control type="text" placeholder="Town" {...register('town')} />
            </Form.Group>
            <Form.Group as={Col} controlId="country">
              <Form.Control type="text" placeholder="Country" {...register('country')} />
            </Form.Group>
            <Form.Group as={Col} controlId="postcode">
              <Form.Control type="text" placeholder="Postcode" {...register('postcode')} />
            </Form.Group>
          </Row>

          <Stack direction="row" spacing={2}>
            <Button variant="outlined" color="inherit" type="button" onClick={() => navigate('/customers')}>
              Cancel
            </Button>
            <Button variant="contained" className='red-bg red-button text-capitalize' type="submit" disabled={loading}>
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Update'}
            </Button>
          </Stack>
        </Form>
      </div>
    </>
  );
}
